/**
 * サブステップの「リンク先画面」 Select の選択肢を 1 箇所で組み立てる pure model。
 *
 * これまで「あるサブステップで選べる kind / 推奨される kind / deprecated 表示になる
 * kind」の解決は、map (`LINKED_PAGE_KIND_MAP`) / GROUPS (`LINKED_PAGE_KIND_GROUPS`) /
 * RECOMMENDED (`RECOMMENDED_LINKED_PAGE_MAP`) / SubStepEditor の render prop の 4 経路に
 * 散っていた (locality の欠如)。 その解決を `buildLinkedPageKindOptions` 1 つに束ね、
 * SubStepEditor は結果を `<optgroup>` / `<option>` へ描くだけにする。
 *
 * この model が SoT に持つ振る舞い:
 *   - 推奨挿入: 大ステップタイトル → 推奨 kind 群 (`RECOMMENDED_LINKED_PAGE_MAP`) を
 *     GROUPS の item へ解決して先頭に挿入する (`resolveRecommendedLinkedPageKinds` +
 *     `buildRecommendedItems`)。
 *   - silent skip 契約: 推奨 kind が GROUPS に含まれない場合はその kind を静かに除外する
 *     (別 slice で GROUPS 拡張と同時に `RECOMMENDED_LINKED_PAGE_MAP` も拡張する契約、
 *      workflow-templates-linked-page-spec-compliance §9 残論点 row 2)。
 *   - deprecated fallback: 現在値が GROUPS + 推奨のいずれにも無い場合
 *     (= deprecated PLAN_SUBMISSION = 15 を持つ overlay tenant 等) は、現在値を先頭に
 *      disabled option として出すための `{ kind, labelKey }` を返す (ADR 「保存で自動修復」
 *      経路の可視化)。 該当しなければ `deprecatedOption` は undefined。
 *
 * GROUPS / RECOMMENDED / 推奨解決はこの model が SoT。 旧 import 元
 * (`constants/presets`) は互換のため本 model から re-export する。
 */
import { LinkedPageKind } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'

import {
  LINKED_PAGE_KIND_MAP,
  linkedPageEntryLabelKey,
  linkedPageLabelKey,
} from '~/models/linkedPage'

// ─── リンク先画面グループ（AC-010b 7 メインカテゴリ）─────────────────────
//
// spec workflow-templates.md AC-010b で定義された 7 メインカテゴリ
// (需要想定 / 供給力 / 市場取引 / 計画提出 / 同時同量 / データ管理 / 顧客) と、
// それぞれのカテゴリに属する画面 (§サブステップ リンク先画面一覧 (P0) のうち
// enum + routePaths キーが定義済みの 22 画面) のみを含める。
// routePaths に登場しても実 page コンポーネントが未実装のもの
// (planSplitSupply / adminDemandGroups 等) は routeChildren により PlaceholderPage
// にフォールバックするが、 spec P0 上の選択肢として残置する
// (workflow-template-linked-page-8categories 2026-08-03 PdM 判断)。
//
// 除外対象 (workflow-templates-linked-page-spec-compliance §5):
//   - spec AC-010b 明示除外のカテゴリ (ダッシュボード / 収益管理 / 顧客管理詳細 /
//     システム管理 / 個人設定 / お知らせ / 業務ワークフロー) の全 kind
//   - deprecated PLAN_SUBMISSION (`/plan/submission` はマップに残置し overlay
//     data 互換を保つが選択肢からは除外、 新規選択は SUBMISSION_MANAGEMENT を使う)

export type LinkedPageKindItem = {
  kind: LinkedPageKind
  /** routePaths の name i18n key から t() で導出するラベル（表示時に t(routeNameKey) で解決） */
  routeNameKey: string
}

export type LinkedPageKindGroup = {
  /** カテゴリ名の i18n key（表示時に t(groupLabelKey) で解決） */
  groupLabelKey: string
  items: LinkedPageKindItem[]
}

const GROUP_KEY_PREFIX = 'page:adminWorkflowTemplates.subStep.linkedPageGroup'

function toItem(
  kind: Exclude<LinkedPageKind, LinkedPageKind.UNSPECIFIED>,
): LinkedPageKindItem {
  // LINKED_PAGE_KIND_MAP は UNSPECIFIED を除く全 kind を必須キーに持つ exhaustive map。
  // toItem は LINKED_PAGE_KIND_GROUPS からのみ呼ばれる。
  // 雛形の選択肢は断面を持たないので、 断面別の kind も単日の画面名で出す。
  return {
    kind,
    routeNameKey: linkedPageEntryLabelKey(LINKED_PAGE_KIND_MAP[kind]),
  }
}

/**
 * 運用者向け Select の選択肢グループ (spec AC-010b 8 メインカテゴリ)。
 * カテゴリ名・画面名とも i18n key で持ち、 ハードコードしない。
 * 表示順は spec AC-010b の 8 メインカテゴリの順序に従う。
 */
export const LINKED_PAGE_KIND_GROUPS: LinkedPageKindGroup[] = [
  {
    groupLabelKey: `${GROUP_KEY_PREFIX}.demandForecast`,
    items: [
      toItem(LinkedPageKind.DEMAND_FORECAST_DAILY),
      toItem(LinkedPageKind.DEMAND_SINGULAR_DAY),
    ],
  },
  {
    // #4001: spec §サブステップ リンク先画面一覧 カテゴリ 2 (供給力) の 3 画面を
    // 表の順で並べる (取引契約管理 / 48点データ / 需給ギャップ)。
    // 需給ギャップ (SUPPLY_GAP) は AC-WFT-010c の推奨マッピング
    // 「供給力確認 → 取引契約管理 / 48点データ / 需給ギャップ」 の 3 画面目でもあり、
    // GROUPS に無いと SubStepEditor の推奨 optgroup から silently skip される。
    groupLabelKey: `${GROUP_KEY_PREFIX}.supply`,
    items: [
      toItem(LinkedPageKind.SUPPLY_BILATERAL),
      toItem(LinkedPageKind.SUPPLY_48DATA),
      toItem(LinkedPageKind.SUPPLY_GAP),
    ],
  },
  {
    groupLabelKey: `${GROUP_KEY_PREFIX}.marketTrading`,
    items: [
      toItem(LinkedPageKind.BIDDING_SPOT),
      toItem(LinkedPageKind.BIDDING_INTRADAY),
      toItem(LinkedPageKind.ADMIN_BID_LIMITS),
    ],
  },
  {
    groupLabelKey: `${GROUP_KEY_PREFIX}.planSubmission`,
    items: [
      toItem(LinkedPageKind.SUBMISSION_MANAGEMENT),
      toItem(LinkedPageKind.PLAN_PROCUREMENT),
      toItem(LinkedPageKind.PLAN_GEN_SALES),
      toItem(LinkedPageKind.PLAN_SPLIT_SUPPLY),
    ],
  },
  {
    groupLabelKey: `${GROUP_KEY_PREFIX}.balancing`,
    items: [
      toItem(LinkedPageKind.BALANCE_DEMAND),
      toItem(LinkedPageKind.BALANCE_GENERATION),
      toItem(LinkedPageKind.BALANCE_GENERATION_DETAIL),
      toItem(LinkedPageKind.BALANCE_IMPUTATION_POLICY),
    ],
  },
  {
    groupLabelKey: `${GROUP_KEY_PREFIX}.dataManagement`,
    items: [
      toItem(LinkedPageKind.ADMIN_UPLOADS),
      toItem(LinkedPageKind.ADMIN_OCCTO),
      toItem(LinkedPageKind.ADMIN_ESLJ_ACCOUNTS),
      toItem(LinkedPageKind.ADMIN_WEATHER_FORECAST_POINTS),
    ],
  },
  {
    groupLabelKey: `${GROUP_KEY_PREFIX}.customer`,
    items: [toItem(LinkedPageKind.CUSTOMER_DEMAND_GROUPS)],
  },
  {
    // 承認フローはシステム管理トップの「運用」区分にあるので、 リンク先も「運用」 に置く
    // (spec AC-WFT-010b、 #11098 で #8977 決定 B を撤回)。
    groupLabelKey: `${GROUP_KEY_PREFIX}.operations`,
    items: [toItem(LinkedPageKind.ADMIN_APPROVAL_FLOW)],
  },
]

// ─── 推奨マッピング (AC-010c) ─────────────────────────────────────────────
//
// 大ステップタイトルに応じてサブステップ Select の先頭に挿入する「推奨」
// optgroup の SoT。 spec §機能詳細 3a「大ステップ ↔ リンク先画面 推奨マッピング」。
//
// 「その他」・写像未定義のプリセット外タイトル・未選択 (undefined) では推奨を
// 表示せず 7 メインカテゴリのみを表示する (AC-05)。 推奨外の画面もソフト連動で
// 通常カテゴリ内に重複表示され選択可能 (AC-06)。

/**
 * spec §機能詳細 4a の推奨マッピングの key = 大ステップタイトルプリセット中の P0 6 種。
 */
export type BigStepTitle =
  | '需要想定'
  | '供給力確認'
  | 'スポット入札'
  | '時間前入札'
  | '計画提出'
  | '同時同量監視'

/**
 * 大ステップタイトル → 推奨リンク先画面 kind 群のマップ (AC-010c)。
 */
export const RECOMMENDED_LINKED_PAGE_MAP: Record<
  BigStepTitle,
  LinkedPageKind[]
> = {
  需要想定: [LinkedPageKind.DEMAND_FORECAST_DAILY],
  // #4001: spec §機能詳細 3a は「供給力確認 → 取引契約管理 / 48点データ /
  // 需給ギャップ」 の 3 画面。 順序は同 spec「推奨リンク先が複数ある場合は、
  // 表の順序で表示する」 に従い表の順のまま並べる。
  供給力確認: [
    LinkedPageKind.SUPPLY_BILATERAL,
    LinkedPageKind.SUPPLY_48DATA,
    LinkedPageKind.SUPPLY_GAP,
  ],
  スポット入札: [LinkedPageKind.BIDDING_SPOT],
  時間前入札: [LinkedPageKind.BIDDING_INTRADAY],
  計画提出: [
    LinkedPageKind.SUBMISSION_MANAGEMENT,
    LinkedPageKind.PLAN_PROCUREMENT,
    LinkedPageKind.PLAN_GEN_SALES,
    LinkedPageKind.PLAN_SPLIT_SUPPLY,
  ],
  同時同量監視: [
    LinkedPageKind.BALANCE_DEMAND,
    LinkedPageKind.BALANCE_GENERATION,
    LinkedPageKind.BALANCE_GENERATION_DETAIL,
  ],
}

function isBigStepTitle(value: string): value is BigStepTitle {
  return Object.hasOwn(RECOMMENDED_LINKED_PAGE_MAP, value)
}

/**
 * 与えられた大ステップタイトルに対する推奨 kind の配列を返す純関数。
 * P0 6 種以外 (「その他」・自由入力・未選択 = undefined) は空配列を返す。
 */
export function resolveRecommendedLinkedPageKinds(
  bigStepTitle: string | undefined,
): LinkedPageKind[] {
  if (bigStepTitle === undefined) return []
  if (!isBigStepTitle(bigStepTitle)) return []
  return RECOMMENDED_LINKED_PAGE_MAP[bigStepTitle]
}

// ─── 選択肢の組み立て ─────────────────────────────────────────────────────

/**
 * 推奨 kind 群を GROUPS の `LinkedPageKindItem` へ解決する純関数。
 *
 * routeNameKey の解決経路を GROUPS 1 箇所に集約するため、 各 kind を
 * `LINKED_PAGE_KIND_GROUPS` から検索して対応する item を再利用する。
 * GROUPS に含まれない kind はここで **silently skip** される
 * (= 別 slice で GROUPS 拡張と同時に `RECOMMENDED_LINKED_PAGE_MAP` も拡張する契約、
 *  workflow-templates-linked-page-spec-compliance §9 残論点 row 2)。
 */
export function buildRecommendedItems(
  recommendedKinds: LinkedPageKind[],
): LinkedPageKindItem[] {
  return recommendedKinds
    .map((kind) => {
      for (const group of LINKED_PAGE_KIND_GROUPS) {
        const found = group.items.find((i) => i.kind === kind)
        if (found !== undefined) return found
      }
      return undefined
    })
    .filter((item): item is LinkedPageKindItem => item !== undefined)
}

/**
 * deprecated fallback option の情報。
 * 現在値を先頭に disabled option として表示するための最小情報。
 */
export type DeprecatedLinkedPageOption = {
  kind: LinkedPageKind
  /** t() で解決する i18n key (suffix「（現在値・要更新）」は描画側で付与) */
  labelKey: string
}

/**
 * サブステップ Select の選択肢構築結果。
 */
export type LinkedPageKindOptions = {
  /** 先頭「推奨」 optgroup に並べる item (空なら推奨 optgroup は出さない) */
  recommended: LinkedPageKindItem[]
  /** 通常の 7 メインカテゴリ optgroup */
  groups: LinkedPageKindGroup[]
  /** 現在値が選択肢に無いときの fallback option (無ければ undefined) */
  deprecatedOption?: DeprecatedLinkedPageOption
}

/**
 * 大ステップタイトルと現在値 (linked_page_kind) から、 サブステップ Select に描く
 * 選択肢一式を組み立てる純関数。 map / GROUPS / RECOMMENDED を 1 箇所で束ねる。
 *
 * - `recommended`: 大ステップタイトルの推奨 kind を GROUPS item へ解決 (silent skip 込み)
 * - `groups`: 常に `LINKED_PAGE_KIND_GROUPS` (7 メインカテゴリ)
 * - `deprecatedOption`: 現在値が推奨 + GROUPS のいずれにも無く、 かつ
 *   `linkedPageLabelKey` で label を解決できる場合のみ設定する。 現在値が undefined、
 *   または既に選択肢に含まれる、 または map に無い (label 解決不能) 場合は undefined。
 *
 * @param bigStepTitle - 親大ステップの現在タイトル (推奨マッピングの key)
 * @param currentKind - サブステップの現在の linked_page_kind (未設定は undefined)
 */
export function buildLinkedPageKindOptions(
  bigStepTitle: string | undefined,
  currentKind: LinkedPageKind | undefined,
): LinkedPageKindOptions {
  const recommended = buildRecommendedItems(
    resolveRecommendedLinkedPageKinds(bigStepTitle),
  )

  // 現在値が「推奨 + 通常カテゴリ」 の可視 kind 集合に含まれるか。
  const visibleKinds = new Set<LinkedPageKind>([
    ...recommended.map((item) => item.kind),
    ...LINKED_PAGE_KIND_GROUPS.flatMap((g) => g.items.map((i) => i.kind)),
  ])

  let deprecatedOption: DeprecatedLinkedPageOption | undefined
  if (currentKind !== undefined && !visibleKinds.has(currentKind)) {
    const labelKey = linkedPageLabelKey(currentKind)
    if (labelKey !== null) {
      deprecatedOption = { kind: currentKind, labelKey }
    }
  }

  return {
    recommended,
    groups: LINKED_PAGE_KIND_GROUPS,
    deprecatedOption,
  }
}
